import { useEffect, useRef } from 'react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import { useGetSimulationCueSheetQuery } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { applySimulationCuePlayability } from '@/helpers/simulation-cue-playability.helper';
import { resolveFlowControlSimulationFlow } from '@/helpers/simulation-flow.helper';

/** When live playout ends, restore Control tab simulation defaults (pointer, segment, preview). */
export function useFlowControlLiveToSimulationReset(
  flow: FlowDto | undefined,
  planFlowId: string | undefined,
  performMode: FlowControlPerformMode,
) {
  const hydrateCueSheet = useFlowControlSimulationCueStore((s) => s.hydrateCueSheet);
  const { data: cueSheet } = useGetSimulationCueSheetQuery(planFlowId, {
    enabled: !!planFlowId,
  });
  const prevPerformModeRef = useRef(performMode);

  useEffect(() => {
    const previousMode = prevPerformModeRef.current;
    prevPerformModeRef.current = performMode;
    if (previousMode !== 'play' || performMode !== 'simulation') {
      return;
    }

    useFlowControlMediaExpandStore.getState().setExpandId(null);
    useLiveSequenceIndexStore.getState().reset();
    useFlowControlSimulationCueStore.getState().reset();

    if (!cueSheet || !planFlowId) {
      return;
    }

    const simulationFlow = resolveFlowControlSimulationFlow(flow);
    const rows = simulationFlow ? applySimulationCuePlayability(cueSheet.rows, simulationFlow) : cueSheet.rows;
    hydrateCueSheet(planFlowId, rows, cueSheet.revision, simulationFlow);
  }, [cueSheet, flow, hydrateCueSheet, performMode, planFlowId]);
}
